/**
 * 错误码目录 —— 全站唯一「人话文案」来源。
 *
 * 约定（ARCH §8.2）：
 *  - 所有 service 只允许抛 `MeshLinkError(code)`；
 *  - UI **禁止**直接显示 `err.message`，必须经 `errorCatalog[code]` 取文案与可执行动作。
 */

export type ErrorAction =
  | 'retry'
  | 'topup'
  | 'switchRelay'
  | 'unlock'
  | 'none';

export type ErrorCode =
  | 'E_BALANCE_INSUFFICIENT'
  | 'E_NONCE_CONFLICT'
  | 'E_CHANNEL_INSUFFICIENT'
  | 'E_RELAY_UNAVAILABLE'
  | 'E_CONTENT_FETCH_TIMEOUT'
  | 'E_PASSWORD_INVALID'
  | 'E_LOCKED'
  | 'E_SIGNATURE_INVALID'
  | 'E_MNEMONIC_INVALID'
  | 'E_PEERID_INVALID'
  | 'E_CONTENT_TOO_LARGE'
  | 'E_FAUCET_USED'
  | 'E_DID_ALREADY_REGISTERED'
  | 'E_CHAIN_UNREACHABLE'
  // —— 创作者付费内容（V1）——
  | 'E_CONTENT_NOT_PAID'
  | 'E_SELF_PURCHASE'
  | 'E_PAID_DELIVERY_PENDING'
  // —— 以下为工程内部补充，同样必须走 catalog ——
  | 'E_IDENTITY_NOT_FOUND'
  | 'E_IDENTITY_EXISTS'
  | 'E_STORAGE_UNAVAILABLE'
  | 'E_INVALID_INPUT'
  | 'E_NOT_IMPLEMENTED'
  | 'E_NOT_UNLOCKED'
  // —— 离线扫码互联（OfflineQR，见 docs/ARCH_OfflineQR_Mesh.md §五 Q1）——
  | 'E_QR_INVALID_FORMAT'
  | 'E_QR_UNSUPPORTED_VERSION'
  | 'E_QR_FINGERPRINT_MISMATCH'
  | 'E_QR_EXPIRED'
  | 'E_QR_TOO_LONG'
  | 'E_QR_CAMERA_DENIED'
  | 'E_QR_CAMERA_UNSUPPORTED'
  | 'E_JOIN_TLS_UNTRUSTED'
  | 'E_JOIN_DIAL_TIMEOUT'
  | 'E_LAN_INFO_UNAVAILABLE'
  | 'E_UNKNOWN';

export interface ErrorEntry {
  /** 人话文案（UI 唯一可展示的错误文本） */
  text: string;
  /** 可执行动作，UI 据此渲染「重试 / 充值 / 切换中继 / 解锁」按钮 */
  action: ErrorAction;
}

export const errorCatalog: Record<ErrorCode, ErrorEntry> = {
  E_BALANCE_INSUFFICIENT: { text: '余额不足，无法支付这笔费用', action: 'topup' },
  E_NONCE_CONFLICT: { text: '交易序号冲突，请重试', action: 'retry' },
  E_CHANNEL_INSUFFICIENT: { text: '支付通道余额不足（连接不会中断）', action: 'topup' },
  E_RELAY_UNAVAILABLE: { text: '所有中继暂不可用，消息已本地排队', action: 'switchRelay' },
  E_CONTENT_FETCH_TIMEOUT: { text: '内容拉取超时', action: 'retry' },
  E_PASSWORD_INVALID: { text: '口令错误，请重试', action: 'none' },
  E_LOCKED: { text: '已锁定，请解锁后操作', action: 'unlock' },
  E_SIGNATURE_INVALID: { text: '签名验证失败，内容已丢弃', action: 'none' },
  E_MNEMONIC_INVALID: { text: '助记词无效，请检查单词与顺序', action: 'none' },
  E_PEERID_INVALID: { text: 'PeerID 格式不正确', action: 'none' },
  E_CONTENT_TOO_LARGE: { text: '内容过大（超过 4MB 上限），请压缩后重试', action: 'none' },
  E_FAUCET_USED: { text: '该地址已领取过新手奖励', action: 'none' },
  E_DID_ALREADY_REGISTERED: { text: '该身份已注册（幂等，无需重复）', action: 'none' },
  E_CHAIN_UNREACHABLE: { text: '链服务不可达，已切换本地账本', action: 'retry' },
  // —— 创作者付费内容（文案均为「人话 + 可执行」）——
  E_CONTENT_NOT_PAID: { text: '这条内容不是付费内容，无需购买', action: 'none' },
  E_SELF_PURCHASE: { text: '这是你自己的作品，无需购买', action: 'none' },
  E_PAID_DELIVERY_PENDING: { text: '已付款，正在等待作者交付内容密钥', action: 'retry' },
  E_IDENTITY_NOT_FOUND: { text: '本机还没有身份，请先创建或导入', action: 'none' },
  E_IDENTITY_EXISTS: { text: '本机已有身份，如需重建请先清空本地数据', action: 'none' },
  E_STORAGE_UNAVAILABLE: { text: '本地存储不可用，请检查浏览器隐私设置', action: 'retry' },
  E_INVALID_INPUT: { text: '输入有误，请检查后重试', action: 'none' },
  E_NOT_IMPLEMENTED: { text: '该功能尚未实现', action: 'none' },
  E_NOT_UNLOCKED: { text: '身份未解锁，请先解锁后再操作', action: 'unlock' },
  // —— 离线扫码互联（文案均为「人话 + 可执行」，不含技术术语）——
  E_QR_INVALID_FORMAT: { text: '无法识别这个二维码，请重新扫描或改为粘贴链接', action: 'retry' },
  E_QR_UNSUPPORTED_VERSION: { text: '这个二维码来自更新的版本，请让对方重新生成', action: 'none' },
  E_QR_FINGERPRINT_MISMATCH: { text: '二维码可能被他人替换，为安全起见已停止连接', action: 'none' },
  E_QR_EXPIRED: { text: '二维码已过期，请让对方重新生成', action: 'none' },
  E_QR_TOO_LONG: { text: '连接信息过长，无法生成可扫描的二维码', action: 'none' },
  E_QR_CAMERA_DENIED: { text: '没有摄像头权限，请在浏览器里允许后重试', action: 'retry' },
  E_QR_CAMERA_UNSUPPORTED: { text: '当前环境无法使用摄像头，请改为粘贴链接', action: 'none' },
  E_JOIN_TLS_UNTRUSTED: { text: '尚未授权对方的连接证书，按提示授权后再试一次', action: 'retry' },
  E_JOIN_DIAL_TIMEOUT: { text: '连接对方超时，请确认两台设备在同一网络后重试', action: 'retry' },
  E_LAN_INFO_UNAVAILABLE: { text: '没有取到本机局域网地址，请确认本地服务已启动', action: 'retry' },
  E_UNKNOWN: { text: '出现未知错误，请重试', action: 'retry' },
};

/** 类型守卫：任意 unknown 是否是已知错误码。 */
export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(errorCatalog, value);
}

/** 取人话文案（未知码回落到 E_UNKNOWN，永不抛错）。 */
export function errorMessage(code: ErrorCode): string {
  return errorCatalog[code]?.text ?? errorCatalog.E_UNKNOWN.text;
}

/** 取可执行动作。 */
export function errorAction(code: ErrorCode): ErrorAction {
  return errorCatalog[code]?.action ?? 'none';
}
